"use client";

import React, { useState, useEffect, useCallback, useRef, useMemo, useContext } from "react";
import { TpTooltip } from "@tranquility-ai/theme-components/tooltip";
import {
  CopyIcon,
  TrashIcon,
  CheckIcon,
  Article,
} from "@phosphor-icons/react";
import { Button } from "@tranquility-ai/theme-components/button";
import { useTimePilotT } from "@/i18n/useTimePilotT";
import {
  buildSharedDataLocalStorageKey,
  sharedDataCasesMatch,
  useOptionalToolsSharedCaseData,
} from "./tools-shared-case-data";
import { AuthContext } from "@/app/contexts";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const COPY_HISTORY_STORAGE_PREFIX = "tpl-copy-history";
const COPY_HISTORY_MAX_ENTRIES = 50;
const COPY_SIZE_LIMIT_BYTES = 1_048_576; // 1 MB
const PREVIEW_MAX_CHARS = 250;

export function getCopyHistoryStorageKey(
  caseId: string | undefined,
  tenantFriendlyName?: string | null,
): string {
  return buildSharedDataLocalStorageKey(
    COPY_HISTORY_STORAGE_PREFIX,
    caseId,
    tenantFriendlyName,
  );
}

export interface CopyHistoryEntry {
  id: string;
  text: string;
  timestamp: number;
}

function loadCopyHistory(storageKey: string): CopyHistoryEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CopyHistoryEntry[];
    return Array.isArray(parsed)
      ? parsed.slice(0, COPY_HISTORY_MAX_ENTRIES)
      : [];
  } catch {
    return [];
  }
}

function previewText(text: string): string {
  const trimmed = text.trim();
  if (trimmed.length <= PREVIEW_MAX_CHARS) return trimmed;
  return trimmed.slice(0, PREVIEW_MAX_CHARS) + "…";
}

/** Hook for recently copied text state, keyed by caseId. */
export function useCopyHistoryState(caseId: string | undefined) {
  const auth = useContext(AuthContext);
  const remote = useOptionalToolsSharedCaseData();
  const tenantFriendlyName = remote?.tenantFriendlyName ?? auth.tenantName ?? null;
  const storageKey = getCopyHistoryStorageKey(caseId, tenantFriendlyName);
  const useRemoteCopies = Boolean(
    caseId &&
      remote &&
      sharedDataCasesMatch(caseId, remote.caseId, remote.tenantFriendlyName),
  );

  const [localCopyHistory, setLocalCopyHistory] = useState<CopyHistoryEntry[]>(
    () => (useRemoteCopies ? [] : loadCopyHistory(storageKey)),
  );
  const [copiedEntryId, setCopiedEntryId] = useState<string | null>(null);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!useRemoteCopies) {
      setLocalCopyHistory(loadCopyHistory(storageKey));
    }
  }, [storageKey, useRemoteCopies]);

  const copyHistory = useRemoteCopies
    ? remote!.hydrated
      ? remote!.copies
      : []
    : localCopyHistory;
  const setCopyHistory = useRemoteCopies ? remote!.setCopies : setLocalCopyHistory;

  useEffect(() => {
    if (useRemoteCopies) return;
    const handler = (event: StorageEvent) => {
      if (event.key === storageKey && event.newValue != null) {
        try {
          const parsed = JSON.parse(event.newValue) as CopyHistoryEntry[];
          if (Array.isArray(parsed)) {
            setLocalCopyHistory(parsed.slice(0, COPY_HISTORY_MAX_ENTRIES));
          }
        } catch {
          /* ignore */
        }
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [storageKey, useRemoteCopies]);

  const persistCopyHistory = useCallback(
    (next: CopyHistoryEntry[]) => {
      if (useRemoteCopies) {
        return;
      }
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        /* ignore */
      }
    },
    [useRemoteCopies, storageKey],
  );

  const addToCopyHistory = useCallback(
    (text: string) => {
      setCopyHistory((prev) => {
        const now = Date.now();
        const existingIndex = prev.findIndex((e) => e.text === text);
        let next: CopyHistoryEntry[];
        if (existingIndex >= 0) {
          const existing = prev[existingIndex];
          next = [
            { ...existing, timestamp: now },
            ...prev.slice(0, existingIndex),
            ...prev.slice(existingIndex + 1),
          ];
        } else {
          next = [
            { id: crypto.randomUUID(), text, timestamp: now },
            ...prev,
          ].slice(0, COPY_HISTORY_MAX_ENTRIES);
        }
        persistCopyHistory(next);
        return next;
      });
    },
    [persistCopyHistory, setCopyHistory],
  );

  const handleCopyEntryClick = useCallback((entry: CopyHistoryEntry) => {
    // Call writeText synchronously so it runs within the user gesture (required by browser)
    navigator.clipboard
      .writeText(entry.text)
      .then(() => {
        if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
        setCopiedEntryId(entry.id);
        copiedTimeoutRef.current = setTimeout(() => {
          setCopiedEntryId(null);
          copiedTimeoutRef.current = null;
        }, 1500);
      })
      .catch(() => {
        /* ignore */
      });
  }, []);

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
    };
  }, []);

  const handleRemoveEntry = useCallback(
    (entryId: string) => {
      setCopyHistory((prev) => {
        const next = prev.filter((e) => e.id !== entryId);
        persistCopyHistory(next);
        return next;
      });
    },
    [persistCopyHistory, setCopyHistory],
  );

  const remoteRefetch = remote?.refetch;
  const refetch = useCallback(() => {
    if (useRemoteCopies && remoteRefetch) {
      void remoteRefetch();
      return;
    }
    setLocalCopyHistory(loadCopyHistory(storageKey));
  }, [useRemoteCopies, remoteRefetch, storageKey]);

  return useMemo(
    () => ({
      copyHistory,
      copiedEntryId,
      addToCopyHistory,
      handleCopyEntryClick,
      handleRemoveEntry,
      refetch,
    }),
    [
      copyHistory,
      copiedEntryId,
      addToCopyHistory,
      handleCopyEntryClick,
      handleRemoveEntry,
      refetch,
    ],
  );
}

/** Return type of useCopyHistoryState for use when injecting shared state. */
export type CopyHistoryState = ReturnType<typeof useCopyHistoryState>;

/**
 * Dispatch this event from any programmatic clipboard write (e.g. copy button)
 * so the CopyHistoryListener can capture it alongside native Cmd+C / right-click copies.
 */
export const COPY_TO_HISTORY_EVENT = "tpl-copy-to-history";

export function dispatchCopyToHistory(text: string) {
  if (typeof document === "undefined") return;
  document.dispatchEvent(
    new CustomEvent(COPY_TO_HISTORY_EVENT, { detail: { text } }),
  );
}

/** Registers a global copy listener that adds selections to the current case's copy history.
 * Pass addToCopyHistory from the same useCopyHistoryState that feeds the list so the list updates. */
export function CopyHistoryListener({
  addToCopyHistory,
}: {
  addToCopyHistory: (text: string) => void;
}) {
  const addToCopyHistoryRef = useRef(addToCopyHistory);
  addToCopyHistoryRef.current = addToCopyHistory;

  useEffect(() => {
    const addToHistory = (text: string) => {
      const trimmed = text?.trim();
      if (!trimmed) return;
      const bytes = new TextEncoder().encode(trimmed).length;
      if (bytes > COPY_SIZE_LIMIT_BYTES) return;
      addToCopyHistoryRef.current(trimmed);
    };

    const handler = (e: ClipboardEvent) => {
      // Read selection synchronously while it still exists, but defer addToHistory until after
      // the browser's default copy action runs. Updating React state in the capture phase
      // (shared tools bundle / drawer) can re-render the chat and clear the selection before
      // the clipboard is written, which breaks Cmd+C / context-menu copy.
      const target = e.target;
      if (
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLInputElement
      ) {
        const start = target.selectionStart ?? 0;
        const end = target.selectionEnd ?? 0;
        if (start !== end) {
          const text = target.value.slice(start, end);
          setTimeout(() => addToHistory(text), 0);
        }
        return;
      }
      const selectionText = document.getSelection()?.toString()?.trim();
      if (selectionText) {
        const text = selectionText;
        setTimeout(() => addToHistory(text), 0);
        return;
      }
      // Fallback: selection may be cleared in some browsers/contexts; read from clipboard
      // after the copy event completes (clipboard is updated by then)
      setTimeout(() => {
        navigator.clipboard
          .readText()
          .then(addToHistory)
          .catch(() => {});
      }, 0);
    };

    const customHandler = (e: Event) => {
      const text = (e as CustomEvent<{ text: string }>).detail?.text;
      if (text) addToHistory(text);
    };

    document.addEventListener("copy", handler, true);
    document.addEventListener(COPY_TO_HISTORY_EVENT, customHandler);
    return () => {
      document.removeEventListener("copy", handler, true);
      document.removeEventListener(COPY_TO_HISTORY_EVENT, customHandler);
    };
  }, []);

  return null;
}

export interface RecentlyCopiedContentProps {
  copyHistory: CopyHistoryEntry[];
  copiedEntryId: string | null;
  onCopyEntry: (entry: CopyHistoryEntry) => void;
  onRemoveEntry: (entryId: string) => void;
  /** When the event modal is open for editing, applies text to the Description field. */
  applyTextToEventDescription?: (text: string) => boolean;
  eventDescriptionApplyReady?: boolean;
}

export function RecentlyCopiedContent({
  copyHistory,
  copiedEntryId,
  onCopyEntry,
  onRemoveEntry,
  applyTextToEventDescription,
  eventDescriptionApplyReady = false,
}: RecentlyCopiedContentProps) {
  const t = useTimePilotT();
  const [appliedToDescriptionEntryId, setAppliedToDescriptionEntryId] =
    useState<string | null>(null);

  const handleApplyEntryToEventDescription = useCallback(
    (entry: CopyHistoryEntry) => {
      if (!applyTextToEventDescription || !eventDescriptionApplyReady) return;
      const ok = applyTextToEventDescription(entry.text);
      if (!ok) return;
      setAppliedToDescriptionEntryId(entry.id);
      setTimeout(() => setAppliedToDescriptionEntryId(null), 1500);
    },
    [applyTextToEventDescription, eventDescriptionApplyReady],
  );

  return (
    <div className="flex flex-1 min-h-0 flex-col overflow-hidden">
      <div className="shrink-0 px-3 py-2 border-b border-white/20">
        <span className="text-white/80 font-light text-[13px] tracking-wider">
          {t("recentlyCopied.title")}
        </span>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
        {copyHistory.length === 0 ? (
          <p className="px-3 py-4 text-white/50 text-xs">
            {t("recentlyCopied.empty")}
          </p>
        ) : (
          <ul className="flex flex-col gap-0 list-none pl-0">
            {copyHistory.map((entry) => (
              <li
                key={entry.id}
                className="flex items-start gap-2 border-b border-white/10 px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <p
                    className="text-white/90 text-xs line-clamp-2 break-words"
                    title={entry.text}
                  >
                    {previewText(entry.text)}
                  </p>
                  <p className="text-white/50 text-[11px] mt-1">
                    {dayjs(entry.timestamp).fromNow()}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                  {copiedEntryId === entry.id ? (
                    <span
                      className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-white/70"
                      aria-live="polite"
                    >
                      <CheckIcon size={14} weight="bold" />
                      {t("recentlyCopied.copied")}
                    </span>
                  ) : (
                    <Button
                      type="button"
                      iconOnly
                      size="sm"
                      style="text"
                      color="light"
                      background="inverse"
                      onClick={() => onCopyEntry(entry)}
                      aria-label={t("favorites.copy")}
                    >
                      <CopyIcon weight="duotone" />
                    </Button>
                  )}
                  <TpTooltip
                    content={
                      eventDescriptionApplyReady
                      ? t("recentlyCopied.applyReady")
                      : t("recentlyCopied.applyNeedsEvent")
                    }
                    placement="left"
                    delayDuration={0}
                  >
                    <Button
                      type="button"
                      iconOnly
                      size="sm"
                      style="text"
                      color="light"
                      background="inverse"
                      onClick={() => handleApplyEntryToEventDescription(entry)}
                      disabled={!eventDescriptionApplyReady}
                      aria-label={t("favorites.applyDescription")}
                    >
                      {appliedToDescriptionEntryId === entry.id ? (
                        <CheckIcon weight="bold" className="text-green-500" />
                      ) : (
                        <Article size={16} weight="duotone" />
                      )}
                    </Button>
                  </TpTooltip>
                  <Button
                    type="button"
                    iconOnly
                    size="sm"
                    style="text"
                    color="danger"
                    background="inverse"
                    onClick={() => onRemoveEntry(entry.id)}
                    aria-label={t("recentlyCopied.remove")}
                  >
                    <TrashIcon weight="duotone" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
